import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { tokenStorage } from '@utils/tokenStorage';
import type { PageResponse } from '@models/api';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const API = axios.create({
  baseURL: API_BASE_URL,
});

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
};

let refreshAccessTokenPromise: Promise<string> | null = null;

/**
 * Endpoints that should NOT trigger session-expired redirects.
 * These are public or auth-related endpoints that can be called without an active session.
 */
const PUBLIC_ENDPOINTS = [
  '/v1/auth/sign-in',
  '/v1/auth/captcha',
  '/v1/auth/refresh',
  '/v1/users/recover-password',
];

const isPublicEndpoint = (url: string) =>
  PUBLIC_ENDPOINTS.some((p) => url.includes(p));

const buildReturnTo = () =>
  encodeURIComponent(window.location.pathname + window.location.search);

const redirectToSessionExpiredLogin = () => {
  if (window.location.pathname.startsWith('/login')) return;

  const returnTo = buildReturnTo();
  window.location.replace(`/login?reason=session_expired&returnTo=${returnTo}`);
};

/**
 * Refresh access token using the stored refresh token.
 *
 * A shared promise prevents a burst of simultaneous 401 responses from
 * sending multiple refresh requests.
 */
export const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = tokenStorage.getRefreshToken();

  if (!refreshToken) {
    throw new Error('No refresh token found.');
  }

  if (!refreshAccessTokenPromise) {
    refreshAccessTokenPromise = axios
      .post<PageResponse<string>>(
        '/v1/auth/refresh',
        { refreshToken },
        { baseURL: API_BASE_URL },
      )
      .then((res) => {
        const nextAccessToken = res.data?.data?.[0];

        if (!nextAccessToken) {
          throw new Error('Refresh response did not include an access token.');
        }

        tokenStorage.setAccessToken(nextAccessToken);
        return nextAccessToken;
      })
      .finally(() => {
        refreshAccessTokenPromise = null;
      });
  }

  return refreshAccessTokenPromise;
};

API.interceptors.request.use((config) => {
  const token = tokenStorage.getAccessToken();
  if (!token) return config;

  config.headers = config.headers ?? {};
  (config.headers as any).Authorization = `Bearer ${token}`;
  return config;
});

API.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error?.response?.status;
    const config = error.config as RetryableRequestConfig | undefined;
    const url = config?.url ?? '';

    const isPublic = isPublicEndpoint(url);

    // The backend may return 403 for an invalid/expired session on /users/me (policy-specific).
    // We treat it as "session expired" to provide a consistent UX.
    const isMeEndpoint = url.includes('/users/me'); // covers both "/v1/users/me" and "/api/v1/users/me"

    const shouldTryRefresh =
      !isPublic &&
      config?._retry !== true &&
      (status === 401 || (isMeEndpoint && status === 403));

    if (shouldTryRefresh && config) {
      try {
        const nextAccessToken = await refreshAccessToken();

        config._retry = true;
        config.headers = config.headers ?? {};
        (config.headers as any).Authorization = `Bearer ${nextAccessToken}`;

        return API(config);
      } catch {
        tokenStorage.clearAll();
        redirectToSessionExpiredLogin();
      }
    }

    // If a request still receives 401 after a successful refresh/retry,
    // the session is no longer recoverable from the client side.
    if (!isPublic && config?._retry === true && status === 401) {
      tokenStorage.clearAll();
      redirectToSessionExpiredLogin();
    }

    if (!isPublic && isMeEndpoint && (status === 401 || status === 403)) {
      tokenStorage.clearAll();
      redirectToSessionExpiredLogin();
    }

    return Promise.reject(error);
  }
);

export default API;
